import { computed, watch } from "vue";
import { ACTIVITY_SECTION_LABELS, ACTIVITY_SECTION_ORDER, } from "@/navigation/activitiesRegistry";
import { groupKeyForCurso } from "@/preferences/groupKey";
import { defaultPinnedModuleIds, filterDrawerGroupsByVisibility, isBankStatementsVisible, isGroupModuleEnabled, listAvailableModules, listGroupConfigurableModules, listVisibleModules, } from "@/preferences/moduleVisibility";
import { useCursoStore } from "@/store/cursoStore";
import { useUiPreferencesStore } from "@/store/uiPreferencesStore";
import { useUserStore } from "@/store/userStore";
import { canActAsDirectivaGestion, effectiveRolesForAccess, } from "@/utils/courseRoles";
import { isCpaDirectiva, isFuncionariosCourse, isKioskCourse } from "@/utils/parseCourseCode";
const CONFIG_SECTION_ORDER = [
    ...ACTIVITY_SECTION_ORDER,
    "treasury",
    "staff",
];
const groupBySection = (items) => CONFIG_SECTION_ORDER.map((id) => ({
    id,
    labelKey: ACTIVITY_SECTION_LABELS[id],
    items: items.filter((item) => item.section === id),
})).filter((group) => group.items.length > 0);
export const useModuleVisibility = () => {
    const cursoStore = useCursoStore();
    const userStore = useUserStore();
    const prefsStore = useUiPreferencesStore();
    const curso = computed(() => (cursoStore.selectedCursoData ||
        cursoStore.getCursoData()));
    const userEmail = computed(() => userStore.email || "");
    const groupKey = computed(() => groupKeyForCurso(curso.value));
    watch(groupKey, () => {
        void prefsStore.ensureGroupSettingsLoaded(curso.value);
    }, { immediate: true });
    const activityCtx = computed(() => ({
        curso: curso.value,
        roles: effectiveRolesForAccess(curso.value, userEmail.value),
        userEmail: userEmail.value,
    }));
    const groupSettings = computed(() => prefsStore.groupSettings(groupKey.value));
    const availableModules = computed(() => listAvailableModules(activityCtx.value, groupSettings.value));
    const groupConfigurableModules = computed(() => listGroupConfigurableModules(activityCtx.value));
    const pinnedIds = computed(() => prefsStore.pinnedModules(userEmail.value, groupKey.value));
    const fallbackPinned = computed(() => defaultPinnedModuleIds(activityCtx.value, availableModules.value));
    const visibleModules = computed(() => listVisibleModules(availableModules.value, pinnedIds.value, activityCtx.value));
    const canEditGroupModules = computed(() => isCpaDirectiva(curso.value) ||
        canActAsDirectivaGestion(curso.value, userEmail.value));
    const groupedVisible = computed(() => groupBySection(visibleModules.value));
    const groupedAvailable = computed(() => groupBySection(availableModules.value));
    const groupedGroupSettings = computed(() => groupBySection(groupConfigurableModules.value));
    const showBankStatements = computed(() => isBankStatementsVisible(groupSettings.value));
    const isPinned = (moduleId) => {
        const ids = pinnedIds.value ?? fallbackPinned.value;
        return ids.includes(moduleId);
    };
    const togglePinned = (moduleId, visible) => {
        prefsStore.togglePinnedModule(userEmail.value, groupKey.value, moduleId, visible, fallbackPinned.value);
    };
    const isGroupEnabled = (mod) => isGroupModuleEnabled(mod, groupSettings.value);
    const isTreasuryEnabledFlag = computed(() => groupSettings.value.treasury.enabled !== false);
    const setGroupEnabled = (moduleId, enabled) => {
        prefsStore.setGroupModuleEnabled(groupKey.value, moduleId, enabled, curso.value);
    };
    const applyDrawerVisibility = (groups) => filterDrawerGroupsByVisibility(groups, {
        visibleIds: new Set(visibleModules.value.map((mod) => mod.id)),
        groupSettings: groupSettings.value,
        skipPersonalPins: isFuncionariosCourse(curso.value) || isKioskCourse(curso.value),
    });
    return {
        curso,
        groupKey,
        availableModules,
        visibleModules,
        groupedVisible,
        groupedAvailable,
        groupedGroupSettings,
        canEditGroupModules,
        isPinned,
        togglePinned,
        isGroupEnabled,
        setGroupEnabled,
        isTreasuryEnabled: isTreasuryEnabledFlag,
        showBankStatements,
        applyDrawerVisibility,
        groupSettings,
    };
};
